import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Sparkles, MessageCircle, Loader2, FileText, Activity, Target, Gift,
  Lightbulb, ArrowRight,
} from 'lucide-react';
import { useTranslation } from '../i18n/LanguageContext';
import { joinWaitlist } from '../lib/waitlist';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// WhatsApp number that users are redirected to after their prescription is ready
const SUPPORT_WHATSAPP_NUMBER = '918308595553';

const GEN_STEPS = ['rx.gen.step1', 'rx.gen.step2', 'rx.gen.step3', 'rx.gen.step4'];

export default function PrescriptionUnlock({ assessmentId }) {
  const { t, lang } = useTranslation();
  // stage: 'locked' | 'form' | 'generating' | 'ready'
  const [stage, setStage] = useState('locked');
  const [form, setForm] = useState({ name: '', age: '', whatsapp: '', email: '' });
  const [submitting, setSubmitting] = useState(false);
  const [genStep, setGenStep] = useState(0);
  const [reportInfo, setReportInfo] = useState({ slug: null, url: null, whatsappDelivery: null });
  const pollRef = useRef(null);
  const pollTickRef = useRef(0);

  const startPolling = useCallback(() => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollTickRef.current = 0;
    setGenStep(0);
    pollRef.current = setInterval(async () => {
      pollTickRef.current += 1;
      const nextStep = Math.min(GEN_STEPS.length - 1, Math.floor(pollTickRef.current / 1));
      setGenStep(nextStep);
      try {
        const res = await axios.get(`${API}/prescription/${assessmentId}/status`);
        if (res.data?.status === 'ready') {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setReportInfo({
            slug: res.data.report_slug,
            url: res.data.report_url,
            whatsappDelivery: res.data.whatsapp_delivery,
          });
          setStage('ready');
          toast.success(t('rx.ready.title'));
        } else if (res.data?.status === 'failed') {
          clearInterval(pollRef.current);
          pollRef.current = null;
          toast.error(t('rx.error.generation'));
        }
      } catch (_) { /* keep polling */ }
    }, 1500);
  }, [assessmentId, t]);

  // Auto-detect ready state on mount
  useEffect(() => {
    if (!assessmentId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/prescription/${assessmentId}/status`);
        if (cancelled) return;
        if (res.data?.status === 'ready') {
          setReportInfo({
            slug: res.data.report_slug,
            url: res.data.report_url,
            whatsappDelivery: res.data.whatsapp_delivery,
          });
          setStage('ready');
        } else if (res.data?.status === 'generating') {
          setStage('generating');
          startPolling();
        }
      } catch (_) { /* keep locked */ }
    })();
    return () => {
      cancelled = true;
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [assessmentId, startPolling]);

  const handleSubmitForm = useCallback(async (e) => {
    e?.preventDefault();
    const f = form;
    if (!f.name.trim() || !f.age || !f.whatsapp.trim()) {
      toast.error(t('rx.form.required'));
      return;
    }
    if (f.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email.trim())) {
      toast.error(t('rx.form.email.invalid'));
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/personalization`, {
        assessment_id: assessmentId,
        name: f.name.trim(),
        age: parseInt(f.age, 10),
        whatsapp: f.whatsapp.trim(),
        email: f.email.trim() || null,
        language: lang,
      });
      setStage('generating');
      startPolling();
    } catch (err) {
      console.error('Personalization save failed:', err);
      toast.error(t('rx.error.network'));
    } finally {
      setSubmitting(false);
    }
  }, [assessmentId, form, lang, startPolling, t]);

  // ---------------- RENDER ----------------
  const stageNode =
    stage === 'ready' ? <ReadyCard t={t} reportInfo={reportInfo} /> :
    stage === 'generating' ? <GeneratingCard t={t} step={genStep} /> :
    stage === 'form' ? (
      <PersonalizationModal
        t={t}
        form={form}
        setForm={setForm}
        onSubmit={handleSubmitForm}
        onCancel={() => setStage('locked')}
        submitting={submitting}
      />
    ) : <UnlockCard t={t} onUnlock={() => setStage('form')} />;

  // Feature flag: Clinity Clinics waitlist section hidden until we're ready to launch.
  // To re-enable later, set SHOW_CLINITY_CLINICS to true (code + data are preserved).
  const SHOW_CLINITY_CLINICS = false;

  // Render the form as a modal overlay; everything else stacks above the Premium section
  if (stage === 'form') {
    return (
      <>
        <UnlockCard t={t} onUnlock={() => setStage('form')} />
        {SHOW_CLINITY_CLINICS && <PremiumAccessSection t={t} />}
        {stageNode}
      </>
    );
  }
  return (
    <>
      {stageNode}
      {SHOW_CLINITY_CLINICS && <PremiumAccessSection t={t} />}
    </>
  );
}

/* ---------------- CLINITY CLINICS — LUXURY WELLNESS DESTINATION ---------------- */
const PRIORITY_WA_NUMBER = '918308595553';

// Full-bleed luxury wellness clinic interior — used as immersive architectural backdrop
const CLINICS_BG_IMAGE = 'https://customer-assets-7cd3h4nn.emergentagent.net/job_life-score-hub-5/artifacts/7bst7ax2_WhatsApp%20Image%202026-07-31%20at%2012.14.29%20AM.jpeg';

function PremiumAccessSection({ t }) {
  const message = t('rx.clinics.wa_message');
  const waUrl = `https://wa.me/${PRIORITY_WA_NUMBER}?text=${encodeURIComponent(message)}`;

  const trackWaitlist = () => {
    try { axios.post(`${API}/analytics/track`, { event: 'clinics_waitlist_clicked', data: { surface: 'clinics_hero' } }); } catch (_) { /* noop */ }
    joinWaitlist({ source: 'clinics_hero' });
  };

  return (
    <section
      className="relative mt-16 w-screen left-1/2 right-1/2 -mx-[50vw] overflow-hidden"
      data-testid="clinity-clinics-hero"
      style={{ background: '#0F172A' }}
    >
      {/* Full-bleed edge-to-edge architectural image */}
      <img
        src={CLINICS_BG_IMAGE}
        alt="Clinity Clinics — luxury wellness clinic"
        className="block w-full h-auto select-none"
        draggable="false"
        style={{ objectFit: 'cover', maxHeight: '760px' }}
        data-testid="clinics-bg-image"
      />

      {/* Overlaid content — Clinity Clinics + info + CTA on the image */}
      <div className="absolute inset-0 flex items-end sm:items-center justify-center">
        {/* Soft readable overlay — bottom gradient on mobile, subtle center wash on desktop */}
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              'linear-gradient(180deg, rgba(15,23,42,0.15) 0%, rgba(15,23,42,0.35) 55%, rgba(15,23,42,0.72) 100%)',
          }}
        />

        <div className="relative w-full max-w-3xl mx-auto px-6 md:px-10 pb-10 sm:pb-16 pt-16 sm:pt-24 text-center">
          <div
            className="inline-flex items-center gap-2 text-[10px] sm:text-[11px] font-bold uppercase tracking-[0.32em] text-[#C7D2FE]"
            data-testid="clinics-eyebrow"
            style={{ textShadow: '0 1px 12px rgba(15,23,42,0.4)' }}
          >
            <span className="w-1 h-1 rounded-full bg-[#C7D2FE]" />
            {t('rx.clinics.eyebrow')}
            <span className="w-1 h-1 rounded-full bg-[#C7D2FE]" />
          </div>

          <h2
            className="mt-5 text-[44px] sm:text-[60px] lg:text-[76px] font-bold leading-[1.02] text-white"
            style={{
              fontFamily: 'Outfit, sans-serif',
              letterSpacing: '-0.035em',
              textShadow: '0 2px 24px rgba(15,23,42,0.5)',
            }}
            data-testid="clinics-title"
          >
            {t('rx.clinics.title')}
          </h2>

          <p
            className="mt-5 text-[15px] sm:text-[17px] text-white/90 leading-[1.65] max-w-xl mx-auto"
            data-testid="clinics-subtitle"
            style={{ textShadow: '0 1px 12px rgba(15,23,42,0.55)' }}
          >
            {t('rx.clinics.subtitle')}
          </p>

          <div className="mt-9 sm:mt-10">
            <a
              href={waUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={trackWaitlist}
              data-testid="clinics-waitlist-btn"
              className="group inline-flex items-center justify-center gap-3 px-9 py-4 sm:px-11 sm:py-[18px] rounded-full font-semibold text-[15px] sm:text-base text-white no-underline transition-all duration-300 hover:-translate-y-0.5 active:translate-y-0"
              style={{
                background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)',
                boxShadow: '0 18px 40px -12px rgba(37,211,102,0.55), inset 0 1px 0 rgba(255,255,255,0.18)',
                letterSpacing: '-0.005em',
              }}
            >
              <MessageCircle className="w-[18px] h-[18px]" strokeWidth={2.2} />
              {t('rx.clinics.cta')}
              <ArrowRight className="w-[18px] h-[18px] transition-transform duration-300 group-hover:translate-x-1" strokeWidth={2.2} />
            </a>
            <p
              className="mt-4 text-xs text-white/70"
              style={{ textShadow: '0 1px 10px rgba(15,23,42,0.5)' }}
            >
              {t('rx.clinics.note')}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

export { PremiumAccessSection };

/* ---------------- VALUE-PROP CARD (Free Prescription CTA) ---------------- */
function UnlockCard({ t, onUnlock }) {
  const previewBlocks = [
    { icon: Lightbulb, label: t('rx.locked.preview.diagnosis') },
    { icon: Target,    label: t('rx.locked.preview.rootcause') },
    { icon: FileText,  label: t('rx.locked.preview.systems') },
    { icon: Activity,  label: t('rx.locked.preview.medicines') },
  ];
  const bullets = [
    'rx.locked.bullet.diagnosis',
    'rx.locked.bullet.rootcause',
    'rx.locked.bullet.systems',
    'rx.locked.bullet.medicines',
    'rx.locked.bullet.guidance',
    'rx.locked.bullet.outcome',
  ];
  return (
    <div
      className="relative rounded-3xl overflow-hidden border border-indigo-100"
      style={{
        background: 'linear-gradient(135deg, #EEF2FF 0%, #F5F3FF 100%)',
        boxShadow: '0 24px 60px -20px rgba(79, 70, 229, 0.25)',
      }}
      data-testid="prescription-paywall"
    >
      {/* Preview ghost — what they're going to get */}
      <div className="relative">
        <div className="grid grid-cols-2 gap-3 p-6 select-none" aria-hidden="true">
          {previewBlocks.map((b, i) => (
            <div key={i} className="bg-white/95 rounded-2xl p-4 border border-white">
              <div className="flex items-center gap-2 mb-2">
                <b.icon className="w-5 h-5 text-[#4F46E5]" />
                <span className="text-xs font-bold uppercase tracking-wide text-[#4F46E5]">{b.label}</span>
              </div>
              <div className="h-2 bg-slate-200 rounded w-3/4 mb-1.5" />
              <div className="h-2 bg-slate-200 rounded w-full mb-1.5" />
              <div className="h-2 bg-slate-200 rounded w-5/6" />
            </div>
          ))}
        </div>
        {/* Free badge */}
        <div className="absolute top-3 right-3 px-3 py-1.5 rounded-full bg-emerald-500 text-white text-[10px] font-bold uppercase tracking-wider flex items-center gap-1.5 shadow-lg">
          <Gift className="w-3 h-3" />
          {t('rx.locked.free')}
        </div>
      </div>

      <div className="p-8 pt-2">
        <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.18em] text-[#4F46E5] mb-2">
          <Sparkles className="w-3.5 h-3.5" />
          {t('rx.locked.eyebrow')}
        </div>
        <h2 className="text-2xl sm:text-3xl font-bold text-[#0F172A] mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
          {t('rx.locked.title')}
        </h2>
        <p className="text-[15px] text-[#475569] mb-5 max-w-xl">{t('rx.locked.subtitle')}</p>

        <ul className="space-y-2.5 mb-7">
          {bullets.map((k) => (
            <li key={k} className="flex items-start gap-3 text-sm text-[#0F172A]">
              <span className="mt-1 w-1.5 h-1.5 rounded-full bg-gradient-to-br from-[#4F46E5] to-[#9333EA] shrink-0" />
              <span>{t(k)}</span>
            </li>
          ))}
        </ul>

        <button
          onClick={onUnlock}
          className="w-full px-6 py-4 rounded-full text-white font-semibold text-base md:text-lg transition-all duration-300 hover:-translate-y-0.5 active:translate-y-0 focus:outline-none focus:ring-4 focus:ring-indigo-300/50"
          style={{
            background: 'linear-gradient(135deg, #4F46E5 0%, #7C3AED 60%, #9333EA 100%)',
            boxShadow: '0 18px 40px -12px rgba(79, 70, 229, 0.55)',
          }}
          data-testid="unlock-prescription-btn"
        >
          {t('rx.locked.cta')}
        </button>

        {/* Clinity tagline — Navigating Wellness Together™ */}
        <p
          className="text-center text-[13px] sm:text-sm font-semibold mt-4 italic"
          style={{
            fontFamily: 'Outfit, sans-serif',
            background: 'linear-gradient(135deg, #4F46E5 0%, #7C3AED 50%, #A855F7 100%)',
            WebkitBackgroundClip: 'text',
            backgroundClip: 'text',
            color: 'transparent',
            WebkitTextFillColor: 'transparent',
            letterSpacing: '0.005em',
          }}
          data-testid="clinity-tagline"
        >
          {t('rx.locked.tagline')}
        </p>

        <p className="text-center text-[12px] text-[#64748B] mt-3 font-medium">
          {t('rx.locked.trust')}
        </p>
      </div>
    </div>
  );
}

/* ---------------- PERSONALIZATION MODAL ---------------- */
function PersonalizationModal({ t, form, setForm, onSubmit, onCancel, submitting }) {
  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  return (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm p-0 sm:p-4" data-testid="personalization-modal">
      <div className="w-full sm:max-w-lg bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl max-h-[92vh] overflow-y-auto">
        <div className="sticky top-0 bg-white px-6 pt-6 pb-3 border-b border-slate-100">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-xl sm:text-2xl font-bold text-[#0F172A]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {t('rx.form.title')}
              </h3>
              <p className="text-sm text-[#64748B] mt-1">{t('rx.form.subtitle')}</p>
            </div>
            <button onClick={onCancel} className="text-slate-400 hover:text-slate-700 text-2xl leading-none p-1" aria-label="close" data-testid="form-cancel-btn">×</button>
          </div>
        </div>

        <form className="px-6 py-5 space-y-4" onSubmit={onSubmit}>
          <Field label={t('rx.form.name')} required>
            <input data-testid="form-name" required value={form.name} onChange={upd('name')} placeholder={t('rx.form.name.placeholder')} className={inputCls} autoComplete="name" />
          </Field>

          <Field label={t('rx.form.age')} required>
            <input data-testid="form-age" type="number" inputMode="numeric" min="13" max="100" required value={form.age} onChange={upd('age')} placeholder={t('rx.form.age.placeholder')} className={inputCls} />
          </Field>

          <Field label={t('rx.form.whatsapp')} required>
            <input data-testid="form-whatsapp" required value={form.whatsapp} onChange={upd('whatsapp')} placeholder={t('rx.form.whatsapp.placeholder')} className={inputCls} type="tel" inputMode="tel" autoComplete="tel" />
          </Field>

          <Field label={t('rx.form.email')}>
            <input data-testid="form-email" value={form.email} onChange={upd('email')} placeholder={t('rx.form.email.placeholder')} className={inputCls} type="email" inputMode="email" autoComplete="email" />
          </Field>

          <p className="text-[11px] text-[#94A3B8] leading-relaxed">{t('rx.form.privacy')}</p>

          <div className="flex flex-col sm:flex-row-reverse gap-3 pt-2">
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 px-6 py-3.5 rounded-full text-white font-semibold transition-all hover:-translate-y-0.5 disabled:opacity-60 disabled:translate-y-0"
              style={{
                background: 'linear-gradient(135deg, #4F46E5 0%, #9333EA 100%)',
                boxShadow: '0 14px 30px -10px rgba(79, 70, 229, 0.55)',
              }}
              data-testid="form-submit-btn"
            >
              {submitting ? <Loader2 className="w-5 h-5 animate-spin inline" /> : t('rx.form.continue')}
            </button>
            <button
              type="button"
              onClick={onCancel}
              className="px-6 py-3.5 rounded-full font-medium text-[#475569] hover:bg-slate-50 border border-slate-200"
            >
              {t('rx.form.cancel')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const inputCls = 'w-full px-4 py-3 rounded-xl border border-slate-200 bg-white text-[#0F172A] placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-300 focus:border-indigo-400 transition';

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="block text-[13px] font-medium text-[#334155] mb-1.5">
        {label}{required && <span className="text-[#EF4444] ml-0.5">*</span>}
      </span>
      {children}
    </label>
  );
}

/* ---------------- GENERATING ---------------- */
function GeneratingCard({ t, step }) {
  return (
    <div
      className="rounded-3xl p-10 text-center border border-indigo-100"
      style={{ background: 'linear-gradient(135deg, #EEF2FF 0%, #F5F3FF 100%)', boxShadow: '0 24px 60px -20px rgba(79, 70, 229, 0.25)' }}
      data-testid="prescription-generating"
    >
      <div className="relative w-20 h-20 mx-auto mb-5">
        <div className="absolute inset-0 rounded-full border-4 border-indigo-200" />
        <div className="absolute inset-0 rounded-full border-4 border-transparent border-t-[#4F46E5] animate-spin" />
        <Sparkles className="absolute inset-0 m-auto w-7 h-7 text-[#4F46E5]" />
      </div>
      <h3 className="text-2xl font-bold text-[#0F172A] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
        {t('rx.gen.title')}
      </h3>
      <p className="text-sm text-[#475569] mb-6 max-w-md mx-auto">{t('rx.gen.subtitle')}</p>

      <div className="max-w-sm mx-auto space-y-2.5 text-left">
        {GEN_STEPS.map((k, i) => {
          const done = i < step;
          const active = i === step;
          return (
            <div key={k} className={`flex items-center gap-3 text-sm transition-opacity ${i > step ? 'opacity-40' : 'opacity-100'}`}>
              <span
                className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0 ${
                  done ? 'bg-emerald-500 text-white' : active ? 'bg-[#4F46E5] text-white' : 'bg-slate-200 text-slate-500'
                }`}
              >
                {done ? '✓' : i + 1}
              </span>
              <span className={done ? 'text-[#0F172A]' : active ? 'text-[#0F172A] font-medium' : 'text-[#64748B]'}>{t(k)}</span>
              {active && <Loader2 className="w-3.5 h-3.5 text-[#4F46E5] animate-spin ml-auto" />}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ---------------- READY ---------------- */
function ReadyCard({ t, reportInfo }) {
  const slug = reportInfo?.slug;
  const reportUrl = reportInfo?.url || (slug ? `${window.location.origin}/report/${slug}` : null);
  const waDelivered = reportInfo?.whatsappDelivery === 'sent';
  // Pre-filled WhatsApp message with the report URL — also a failsafe if Twilio delivery failed
  const waText = reportUrl
    ? `Hi Clinity team! My Wellness Prescription is ready. View it here: ${reportUrl}`
    : `Hi Clinity team! My Wellness Prescription is ready.`;
  // Two URL formats — wa.me works on mobile + desktop without API dependency.
  const waUrl = `https://wa.me/${SUPPORT_WHATSAPP_NUMBER}?text=${encodeURIComponent(waText)}`;

  const trackWA = () => {
    try { axios.post(`${API}/analytics/track`, { event: 'whatsapp_clicked', data: { slug, surface: 'ready_card' } }); } catch (_) { /* noop */ }
  };

  return (
    <div
      className="rounded-3xl p-8 border border-emerald-100 text-center"
      style={{ background: 'linear-gradient(135deg, #ECFDF5 0%, #F0FDFA 100%)', boxShadow: '0 24px 60px -20px rgba(16, 185, 129, 0.25)' }}
      data-testid="prescription-ready"
    >
      <div className="w-16 h-16 rounded-full bg-emerald-500 mx-auto mb-4 flex items-center justify-center shadow-lg">
        <svg viewBox="0 0 24 24" className="w-8 h-8 text-white" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
          <path d="M20 6L9 17l-5-5" />
        </svg>
      </div>
      <h3 className="text-2xl font-bold text-[#0F172A] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
        {t('rx.ready.title')}
      </h3>
      <p className="text-sm text-[#475569] mb-4 max-w-md mx-auto">
        {t('rx.ready.subtitle')}
      </p>
      <p className="text-xs text-[#64748B] mb-6 max-w-md mx-auto">
        {t('rx.ready.saved')}
      </p>

      {/* Failsafe: if WhatsApp delivery failed, surface the URL directly */}
      {reportUrl && !waDelivered && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-5 text-left max-w-md mx-auto" data-testid="ready-failsafe">
          <div className="text-[11px] font-bold uppercase tracking-wider text-amber-700 mb-1">{t('rx.ready.failsafe.title')}</div>
          <div className="text-xs text-[#475569] break-all">
            {t('rx.ready.failsafe.body')}{' '}
            <a href={reportUrl} target="_blank" rel="noopener noreferrer" className="text-[#4F46E5] font-medium underline">
              {reportUrl}
            </a>
          </div>
        </div>
      )}

      {/* Primary CTA: Open WhatsApp */}
      <a
        href={waUrl}
        target="_blank"
        rel="noopener noreferrer"
        onClick={trackWA}
        className="inline-flex w-full sm:w-auto items-center justify-center gap-2 px-8 py-4 rounded-full font-semibold text-white text-base transition-all hover:-translate-y-0.5 no-underline mb-3"
        style={{ background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)', boxShadow: '0 14px 30px -10px rgba(37, 211, 102, 0.55)' }}
        data-testid="ready-whatsapp-btn"
      >
        <MessageCircle className="w-5 h-5" />
        {t('rx.ready.whatsapp')}
      </a>

      {/* Secondary action — retake only (Prescription is delivered via WhatsApp) */}
      <div className="flex flex-col sm:flex-row gap-2 justify-center items-center mt-2">
        <a
          href="/assessment"
          className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full font-medium text-[#64748B] text-sm hover:text-[#4F46E5] transition-colors no-underline"
          data-testid="ready-retake-btn"
        >
          {t('rx.ready.retake')}
        </a>
      </div>
    </div>
  );
}
